/**
 * MH-06 Nhập khẩu hàng loạt thiết bị — bốn bước trên CÙNG MỘT màn hình, có thanh
 * tiến trình bước (mục 4.5 đặc tả giao diện).
 *
 *   (1) Tải tệp        — liên kết tải tệp mẫu, nêu rõ giới hạn 5.000 dòng
 *   (2) Đối chiếu cột  — ánh xạ cột trong tệp với trường hệ thống, GHI NHỚ ánh xạ
 *                        đã dùng lần trước
 *   (3) Kiểm tra       — ba nhóm hợp lệ / cảnh báo / lỗi; dòng lỗi nêu TÊN CỘT và
 *                        lý do
 *   (4) Xác nhận       — chỉ nhập dòng hợp lệ và cảnh báo, kết thúc bằng tệp kết
 *                        quả tải về được gồm cả các dòng bị loại kèm lý do
 *
 * Tệp được đọc ở TRÌNH DUYỆT vì bước đối chiếu cột cần biết tiêu đề cột trước khi
 * gửi; phần gửi lên máy chủ là các dòng đã ánh xạ.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { dienVao, vi } from '../ngon-ngu/vi';
import { LoiApi, thietBiApi } from '../api/thiet-bi-api';

const KHOA_NHO_ANH_XA = 'htttn.nhapKhau.anhXaCot';

export const COT_HE_THONG = [
  'cumLoaId',
  'tenThietBi',
  'loaiThietBi',
  'nguonId',
  'maThon',
  'phuongThucKetNoi',
  'maNhaCungCap',
  'adapterId',
  'externalDeviceRef',
  'viDo',
  'kinhDo',
] as const;

interface KetQuaKiemTra {
  tongDong: number;
  soHopLe: number;
  soCanhBao: number;
  soLoi: number;
  chiTiet: { dong: number; cumLoaId: string; ketQua: string; cot: string | null; lyDo: string | null }[];
  soDaNhap: number | null;
}

/**
 * Bọc một ô khi ghi CSV. Bắt buộc bọc khi ô chứa dấu phẩy, dấu ngoặc kép hoặc
 * xuống dòng — cột "viDo, kinhDo" của dòng cảnh báo có dấu phẩy nên nếu không
 * bọc thì tệp kết quả lệch cột và người dùng không nhập lại được.
 */
export function oCsv(gt: string | number): string {
  const s = String(gt);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** Đọc CSV đơn giản: dấu phẩy phân tách, hỗ trợ ô có dấu ngoặc kép. */
export function docCsv(van: string): { tieuDe: string[]; dong: string[][] } {
  const dongVan = van.replace(/\r\n?/g, '\n').split('\n').filter((x) => x.trim() !== '');
  const tach = (d: string): string[] => {
    const o: string[] = [];
    let hien = '';
    let trongNgoac = false;
    for (let i = 0; i < d.length; i += 1) {
      const c = d[i];
      if (c === '"') {
        if (trongNgoac && d[i + 1] === '"') {
          hien += '"';
          i += 1;
        } else trongNgoac = !trongNgoac;
      } else if (c === ',' && !trongNgoac) {
        o.push(hien);
        hien = '';
      } else hien += c;
    }
    o.push(hien);
    return o.map((x) => x.trim());
  };
  const tieuDe = tach(dongVan[0] ?? '');
  return { tieuDe, dong: dongVan.slice(1).map(tach) };
}

export function MH06NhapKhau(): ReactNode {
  const [buoc, datBuoc] = useState<1 | 2 | 3 | 4>(1);
  const [tieuDe, datTieuDe] = useState<string[]>([]);
  const [dongTep, datDongTep] = useState<string[][]>([]);
  const [anhXa, datAnhXa] = useState<Record<string, string>>({});
  const [daNhoAnhXa, datDaNhoAnhXa] = useState(false);
  const [kq, datKq] = useState<KetQuaKiemTra | null>(null);
  const [dangChay, datDangChay] = useState(false);
  const [loi, datLoi] = useState<string | null>(null);

  useEffect(() => {
    try {
      const nho = globalThis.localStorage?.getItem(KHOA_NHO_ANH_XA);
      if (nho) {
        datAnhXa(JSON.parse(nho) as Record<string, string>);
        datDaNhoAnhXa(true);
      }
    } catch {
      // Trình duyệt chặn localStorage: bỏ qua, người dùng ánh xạ lại bằng tay.
    }
  }, []);

  const chonTep = async (tep: File | null): Promise<void> => {
    datLoi(null);
    if (!tep) return;
    try {
      const van = await tep.text();
      const doc = docCsv(van);
      if (doc.tieuDe.length === 0) throw new Error('rỗng');
      datTieuDe(doc.tieuDe);
      datDongTep(doc.dong);
      // Ánh xạ tự động khi tên cột trùng tên trường hệ thống.
      datAnhXa((cu) => {
        const moi = { ...cu };
        for (const c of doc.tieuDe) {
          if (!moi[c] && (COT_HE_THONG as readonly string[]).includes(c)) moi[c] = c;
        }
        return moi;
      });
      datBuoc(2);
    } catch {
      datLoi(vi.nhapKhau.loiDocTep);
    }
  };

  const dungDong = (): Record<string, unknown>[] =>
    dongTep.map((d) => {
      const o: Record<string, unknown> = {};
      tieuDe.forEach((c, i) => {
        const truong = anhXa[c];
        if (truong) o[truong] = d[i] ?? '';
      });
      return o;
    });

  const kiemTra = async (): Promise<void> => {
    datDangChay(true);
    datLoi(null);
    try {
      const r = await thietBiApi.nhapKhau(dungDong(), true);
      datKq(r);
      datBuoc(3);
      try {
        globalThis.localStorage?.setItem(KHOA_NHO_ANH_XA, JSON.stringify(anhXa));
      } catch {
        // Bỏ qua: chỉ mất tiện lợi ghi nhớ ánh xạ.
      }
    } catch (e) {
      datLoi(e instanceof LoiApi ? e.message : vi.chung.loi);
    } finally {
      datDangChay(false);
    }
  };

  const nhapThat = async (): Promise<void> => {
    datDangChay(true);
    datLoi(null);
    try {
      const r = await thietBiApi.nhapKhau(dungDong(), false);
      datKq(r);
      datBuoc(4);
    } catch (e) {
      datLoi(e instanceof LoiApi ? e.message : vi.chung.loi);
    } finally {
      datDangChay(false);
    }
  };

  /** Tệp kết quả gồm cả dòng bị loại kèm lý do, để người dùng sửa và nhập lại. */
  const taiTepLoi = (): void => {
    const dong = (kq?.chiTiet ?? []).filter((x) => x.ketQua !== 'hop_le');
    const csv = [
      [vi.nhapKhau.cotDong, vi.nhapKhau.cotMa, vi.nhapKhau.cotKetQua, vi.nhapKhau.cotCot, vi.nhapKhau.cotLyDo].join(','),
      ...dong.map((x) =>
        [x.dong, x.cumLoaId, x.ketQua, x.cot ?? '', x.lyDo ?? ''].map(oCsv).join(','),
      ),
    ].join('\n');
    const url = URL.createObjectURL(new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'ket-qua-nhap-khau.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const tepMau = (): void => {
    const csv = `${COT_HE_THONG.join(',')}\n862135049283746,Cụm loa thôn Trung Tâm,cum_thu_phat_thanh,H55.101,H55.101.007,truc_tiep,vtc,,,21.592,105.844\n`;
    const url = URL.createObjectURL(new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'mau-nhap-khau-thiet-bi.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <section className="man-hinh" data-man-hinh="MH-06">
      <nav className="vet-duong-dan" aria-label={vi.dieuHuong.thietBi}>
        <Link to="/thiet-bi">{vi.dieuHuong.thietBi}</Link> / <span>{vi.nhapKhau.tieuDe}</span>
      </nav>
      <h1>{vi.nhapKhau.tieuDe}</h1>

      <ol className="thanh-buoc" data-vung="thanh-buoc">
        {[vi.nhapKhau.buoc1, vi.nhapKhau.buoc2, vi.nhapKhau.buoc3, vi.nhapKhau.buoc4].map((n, i) => (
          <li key={n} aria-current={buoc === i + 1 ? 'step' : undefined}>
            ({i + 1}) {n}
          </li>
        ))}
      </ol>

      {loi ? (
        <p className="dai-thong-bao dai-thong-bao--loi" role="alert">
          {loi}
        </p>
      ) : null}

      {buoc === 1 ? (
        <div data-vung="buoc-1">
          <p>{vi.nhapKhau.gioiHan}</p>
          <button type="button" className="nut" onClick={tepMau}>
            {vi.nhapKhau.tepMau}
          </button>
          <p className="truong">
            <label htmlFor="nk-tep">{vi.nhapKhau.chonTep}</label>
            <input
              id="nk-tep"
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => void chonTep(e.target.files?.[0] ?? null)}
            />
          </p>
        </div>
      ) : null}

      {buoc === 2 ? (
        <div data-vung="buoc-2">
          {daNhoAnhXa ? <p className="chu-thich">{vi.nhapKhau.nhoAnhXa}</p> : null}
          <table className="bang">
            <thead>
              <tr>
                <th scope="col">{vi.nhapKhau.cotTrongTep}</th>
                <th scope="col">{vi.nhapKhau.truongHeThong}</th>
              </tr>
            </thead>
            <tbody>
              {tieuDe.map((c) => (
                <tr key={c}>
                  <td>{c}</td>
                  <td>
                    <select
                      value={anhXa[c] ?? ''}
                      aria-label={`${vi.nhapKhau.truongHeThong}: ${c}`}
                      onChange={(e) => datAnhXa((cu) => ({ ...cu, [c]: e.target.value }))}
                    >
                      <option value="">{vi.nhapKhau.boQua}</option>
                      {COT_HE_THONG.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button type="button" className="nut" onClick={() => datBuoc(1)}>
            {vi.nhapKhau.quayLai}
          </button>
          <button
            type="button"
            className="nut nut--chinh"
            disabled={dangChay}
            onClick={() => void kiemTra()}
          >
            {vi.nhapKhau.tiepTuc}
          </button>
        </div>
      ) : null}

      {(buoc === 3 || buoc === 4) && kq ? (
        <div data-vung={`buoc-${buoc}`}>
          <h2>{dienVao(vi.nhapKhau.ketQuaKiemTra, { so: kq.tongDong })}</h2>
          <ul className="ket-qua-lo__so">
            <li data-nhom="hop-le">
              <span>{vi.nhapKhau.hopLe}</span> <strong data-so="hop-le">{kq.soHopLe}</strong>
            </li>
            <li data-nhom="canh-bao">
              <span>{vi.nhapKhau.canhBao}</span> <strong data-so="canh-bao">{kq.soCanhBao}</strong>
            </li>
            <li data-nhom="loi">
              <span>{vi.nhapKhau.loi}</span> <strong data-so="loi">{kq.soLoi}</strong>
            </li>
          </ul>
          <p className="chu-thich">{vi.nhapKhau.ghiChuCanhBao}</p>

          <table className="bang">
            <thead>
              <tr>
                <th scope="col">{vi.nhapKhau.cotDong}</th>
                <th scope="col">{vi.nhapKhau.cotMa}</th>
                <th scope="col">{vi.nhapKhau.cotKetQua}</th>
                <th scope="col">{vi.nhapKhau.cotCot}</th>
                <th scope="col">{vi.nhapKhau.cotLyDo}</th>
              </tr>
            </thead>
            <tbody>
              {kq.chiTiet.slice(0, 200).map((x) => (
                <tr key={`${x.dong}-${x.cumLoaId}`} data-ket-qua={x.ketQua}>
                  <td>{x.dong}</td>
                  <td>
                    <code>{x.cumLoaId}</code>
                  </td>
                  <td>
                    {x.ketQua === 'loi'
                      ? vi.nhapKhau.loi
                      : x.ketQua === 'canh_bao'
                        ? vi.nhapKhau.canhBao
                        : vi.nhapKhau.hopLe}
                  </td>
                  <td>{x.cot ?? ''}</td>
                  <td>{x.lyDo ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <button type="button" className="nut" onClick={taiTepLoi}>
            {vi.nhapKhau.taiTepLoi}
          </button>
          {buoc === 3 ? (
            <button
              type="button"
              className="nut nut--chinh"
              disabled={dangChay || kq.soHopLe + kq.soCanhBao === 0}
              onClick={() => void nhapThat()}
            >
              {dangChay
                ? vi.nhapKhau.dangNhap
                : dienVao(vi.nhapKhau.nhapSoDong, { so: kq.soHopLe + kq.soCanhBao })}
            </button>
          ) : (
            <p className="dai-thong-bao" role="status">
              {dienVao(vi.nhapKhau.daNhap, { so: kq.soDaNhap ?? 0 })}
            </p>
          )}
        </div>
      ) : null}
    </section>
  );
}
